import axios, {
  type AxiosAdapter,
  type AxiosInstance,
  type AxiosResponse,
  type InternalAxiosRequestConfig,
} from 'axios'

const installed = new WeakSet<AxiosInstance>()

function normalizedValue(value: unknown): unknown {
  if (value === undefined) return null

  if (value instanceof URLSearchParams) {
    return [...value.entries()].sort(([left], [right]) => left.localeCompare(right))
  }

  if (Array.isArray(value)) {
    return value.map((entry) => normalizedValue(entry))
  }

  if (value !== null && typeof value === 'object') {
    return Object.keys(value as Record<string, unknown>)
      .sort()
      .reduce<Record<string, unknown>>((acc, key) => {
        acc[key] = normalizedValue((value as Record<string, unknown>)[key])
        return acc
      }, {})
  }

  return value
}

function stableSerialize(value: unknown): string {
  return JSON.stringify(normalizedValue(value))
}

function headerValue(headers: unknown, name: string): string {
  if (!headers) return ''

  const maybeHeaders = headers as { get?: unknown }
  if (typeof maybeHeaders.get === 'function') {
    const fromGetter = maybeHeaders.get(name)
    if (fromGetter !== undefined && fromGetter !== null) {
      return String(fromGetter)
    }
  }

  const lower = name.toLowerCase()
  const direct = Object.entries(headers as Record<string, unknown>).find(
    ([key]) => key.toLowerCase() === lower,
  )?.[1]

  return direct !== undefined && direct !== null ? String(direct) : ''
}

function shouldDedupe(config: InternalAxiosRequestConfig): boolean {
  const method = (config.method ?? 'get').toLowerCase()
  if (method !== 'get' && method !== 'head') return false

  if (config.signal !== undefined) return false
  if (config.responseType !== undefined && config.responseType !== 'json') return false

  return true
}

function dedupeKey(config: InternalAxiosRequestConfig): string {
  return [
    (config.method ?? 'get').toLowerCase(),
    config.baseURL ?? '',
    config.url ?? '',
    stableSerialize(config.params ?? null),
    config.responseType ?? 'json',
    headerValue(config.headers, 'Authorization'),
    headerValue(config.headers, 'X-Tenant-Id'),
    headerValue(config.headers, 'X-Workspace-Id'),
    headerValue(config.headers, 'Accept-Language'),
    headerValue(config.headers, 'X-Locale'),
  ].join('|')
}

function cloneResponseForConfig<T = unknown>(
  response: AxiosResponse<T>,
  config: InternalAxiosRequestConfig,
): AxiosResponse<T> {
  return {
    ...response,
    config,
  }
}

export function installInFlightGetDedupe(instance: AxiosInstance): void {
  if (installed.has(instance)) return
  installed.add(instance)

  const baseAdapter: AxiosAdapter = axios.getAdapter(
    instance.defaults.adapter ?? axios.defaults.adapter,
  )
  const inFlight = new Map<string, Promise<AxiosResponse>>()

  instance.defaults.adapter = async (config) => {
    if (!shouldDedupe(config)) {
      return baseAdapter(config)
    }

    const key = dedupeKey(config)
    const existing = inFlight.get(key)
    if (existing) {
      return existing.then((response) => cloneResponseForConfig(response, config))
    }

    const request = baseAdapter(config)
      .finally(() => {
        inFlight.delete(key)
      })

    inFlight.set(key, request)

    return request
  }
}
